import React, { useState, useEffect } from 'react';
import { Box, Grid, CircularProgress, Alert, Table, TableHead, TableRow, TableCell, TableBody,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  FormControlLabel, Checkbox } from '@mui/material';
import { useHistory, useParams } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import SectionCard from '../../components/SectionCard';
import StatusChip from '../../components/StatusChip';
import InvButton from '../../components/InvButton';
import ConfirmDialog from '../../components/ConfirmDialog';
import BatchChip from '../../components/BatchChip';
import { DetailRow } from '../../components/InvField';
import { getReturn, getReturnItems, confirmReturn, acknowledgeReturn, rejectReturn } from '../../api/dispensing';
import { getStores } from '../../api/stockLedger';
import useItemNames from '../../hooks/useItemNames';
import { resolveDispensingScope } from '../../utils/dispensingEntity';
import { formatDate } from '../../utils';
import useInvRole from '../../hooks/useInvRole';
import { INV_ACTIONS } from '../../utils/inventoryRoles';
import { IPD_COLORS, cardSx } from '../../theme';

function normalizeList(data) {
  return Array.isArray(data) ? data : (data?.data || data?.items || []);
}

function storeLabel(store) {
  if (!store) return null;
  const name = store.store_name || '';
  const code = store.store_code || '';
  return code ? `${name} (${code})` : name || null;
}

export default function ReturnDetail() {
  const { id } = useParams();
  const history = useHistory();
  const { can } = useInvRole();
  const { resolveItem } = useItemNames();
  const [ret, setRet] = useState(null);
  const [retItems, setRetItems] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [actioning, setActioning] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [triggerReversal, setTriggerReversal] = useState(false);
  const [ackOpen, setAckOpen] = useState(false);
  const [ackTriggerReversal, setAckTriggerReversal] = useState(false);
  const [dialog, setDialog] = useState(null);
  const [reason, setReason] = useState('');

  async function load() {
    setLoading(true); setError(null);
    try {
      const [r, items, storesData] = await Promise.all([
        // API: GET /inventory/dispensing/returns/{id}/
        getReturn(id),
        // API: GET /inventory/dispensing/returns/{id}/items/
        getReturnItems(id).catch(e => { console.error('[ReturnDetail] load items failed:', e); return []; }),
        // API: GET /inventory/stores/
        getStores().catch(e => { console.error('[ReturnDetail] load stores failed:', e); return []; }),
      ]);
      setRet(r);
      setRetItems(normalizeList(items));
      setStores(normalizeList(storesData));
    } catch (e) { setError(e.message); } finally { setLoading(false); }
  }

  useEffect(() => { load(); }, [id]);

  function resolveStoreName(storeId) {
    if (!storeId) return null;
    const store = stores.find(s => (s.store_id || s.id) === storeId);
    return storeLabel(store) || storeId;
  }

  async function doConfirm() {
    if (!can(INV_ACTIONS.RETURN_CONFIRM)) return;
    setActioning(true); setActionError(null);
    try {
      // API: POST /inventory/dispensing/returns/{id}/confirm/
      await confirmReturn(id, { trigger_billing_reversal: triggerReversal, remarks: '', ...resolveDispensingScope(ret, stores) });
      setConfirmOpen(false); load();
    } catch (e) {
      console.error('[ReturnDetail] confirm failed:', e);
      setActionError(e.message);
    } finally { setActioning(false); }
  }

  async function doAcknowledge() {
    if (!can(INV_ACTIONS.RETURN_ACKNOWLEDGE)) return;
    setActioning(true); setActionError(null);
    try {
      // API: POST /inventory/dispensing/returns/{id}/acknowledge/
      await acknowledgeReturn(id, { trigger_billing_reversal: ackTriggerReversal, ...resolveDispensingScope(ret, stores) });
      setAckOpen(false); load();
    } catch (e) {
      console.error('[ReturnDetail] acknowledge failed:', e);
      setActionError(e.message);
    } finally { setActioning(false); }
  }

  async function executeAction() {
    if (dialog.action === 'reject' && !can(INV_ACTIONS.RETURN_REJECT)) return;
    setActioning(true); setActionError(null);
    try {
      const scope = resolveDispensingScope(ret, stores);
      // API: POST /inventory/dispensing/returns/{id}/reject/ — reason required
      if (dialog.action === 'reject') await rejectReturn(id, { reason, ...scope });
      setDialog(null); setReason(''); load();
    } catch (e) {
      console.error('[ReturnDetail] action failed:', e);
      setActionError(e.message);
    } finally { setActioning(false); }
  }

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}><CircularProgress sx={{ color: IPD_COLORS.primary }} /></Box>;
  if (error) return <Box sx={{ p: 4 }}><Alert severity="error">{error}</Alert></Box>;
  if (!ret) return null;

  const { status } = ret;
  const actionButtons = [];
  if (status === 'DRAFT' && can(INV_ACTIONS.RETURN_CONFIRM)) actionButtons.push(
    <InvButton key="confirm" size="small" tone="mint" onClick={() => { setTriggerReversal(false); setConfirmOpen(true); }}>
      Confirm Return
    </InvButton>
  );
  if (status === 'RETURNED') {
    if (can(INV_ACTIONS.RETURN_ACKNOWLEDGE)) actionButtons.push(
      <InvButton key="ack" size="small" tone="mint"
        onClick={() => { setAckTriggerReversal(false); setAckOpen(true); }}>
        Acknowledge
      </InvButton>
    );
    if (can(INV_ACTIONS.RETURN_REJECT)) actionButtons.push(
      <InvButton key="reject" size="small" tone="red" variant="outlined"
        onClick={() => setDialog({ action: 'reject', title: 'Reject Return', desc: 'Reject this return?', destructive: true, requireReason: true })}>
        Reject
      </InvButton>
    );
  }

  return (
    <Box>
      <PageHeader eyebrow="Inventory Management › Returns" title={ret.transaction_number}
        actions={
          <>
            <InvButton variant="outlined" tone="teal" size="small" onClick={() => history.goBack()}>Back</InvButton>
            {actionButtons}
          </>
        }
      />
      {actionError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>{actionError}</Alert>}
      <Grid container spacing={2.5}>
        <Grid item xs={12} md={5}>
          <SectionCard title="Details" actions={<StatusChip status={ret.status} />}>
            <DetailRow label="Return Type">{ret.return_type}</DetailRow>
            <DetailRow label="Return To Store">{resolveStoreName(ret.to_store_id)}</DetailRow>
            <DetailRow label="Patient">{ret.patient_id}</DetailRow>
            <DetailRow label="Source Issue">{ret.source_reference_id}</DetailRow>
            <DetailRow label="Date">{formatDate(ret.transaction_date)}</DetailRow>
            <DetailRow label="Remarks">{ret.remarks}</DetailRow>
          </SectionCard>
        </Grid>
        <Grid item xs={12} md={7}>
          <SectionCard title="Items Returned">
            <Table size="small" sx={{ '& td, & th': { borderColor: IPD_COLORS.border, color: IPD_COLORS.textPrimary } }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Item</TableCell>
                  <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Batch</TableCell>
                  <TableCell align="right" sx={{ color: IPD_COLORS.textSecondary }}>Qty</TableCell>
                  <TableCell sx={{ color: IPD_COLORS.textSecondary }}>Reason</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {retItems.length === 0
                  ? <TableRow><TableCell colSpan={4} sx={{ textAlign: 'center', color: IPD_COLORS.textSecondary }}>No items</TableCell></TableRow>
                  : retItems.map((item, i) => (
                    <TableRow key={i}>
                      <TableCell>{resolveItem(item)}</TableCell>
                      <TableCell><BatchChip batchNumber={item.batch_number} expiryDate={item.expiry_date} /></TableCell>
                      <TableCell align="right">{item.requested_quantity ?? item.return_quantity}</TableCell>
                      <TableCell>{item.reason}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </SectionCard>
        </Grid>
      </Grid>

      {/* Confirm needs a billing-reversal toggle, which ConfirmDialog has no slot for. */}
      <Dialog open={confirmOpen} onClose={actioning ? undefined : () => setConfirmOpen(false)}
        PaperProps={{ sx: { ...cardSx, minWidth: 380, p: 1 } }}>
        <DialogTitle sx={{ color: IPD_COLORS.textPrimary }}>Confirm Return</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: IPD_COLORS.textSecondary }}>
            Confirm this return? Items will be credited back to stock.
          </DialogContentText>
          <FormControlLabel sx={{ mt: 1, color: IPD_COLORS.textPrimary }}
            control={<Checkbox checked={triggerReversal} onChange={e => setTriggerReversal(e.target.checked)} />}
            label="Also reverse the associated billing entry" />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <InvButton variant="outlined" tone="teal" onClick={() => setConfirmOpen(false)} disabled={actioning}>Cancel</InvButton>
          <InvButton tone="mint" onClick={doConfirm} loading={actioning}>Confirm</InvButton>
        </DialogActions>
      </Dialog>

      {/* Acknowledge needs a billing-reversal toggle, which ConfirmDialog has no slot for. */}
      <Dialog open={ackOpen} onClose={actioning ? undefined : () => setAckOpen(false)}
        PaperProps={{ sx: { ...cardSx, minWidth: 380, p: 1 } }}>
        <DialogTitle sx={{ color: IPD_COLORS.textPrimary }}>Acknowledge Return</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: IPD_COLORS.textSecondary }}>
            Acknowledge receipt of this return?
          </DialogContentText>
          <FormControlLabel sx={{ mt: 1, color: IPD_COLORS.textPrimary }}
            control={<Checkbox checked={ackTriggerReversal} onChange={e => setAckTriggerReversal(e.target.checked)} />}
            label="Also reverse the associated billing entry" />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <InvButton variant="outlined" tone="teal" onClick={() => setAckOpen(false)} disabled={actioning}>Cancel</InvButton>
          <InvButton tone="mint" onClick={doAcknowledge} loading={actioning}>Acknowledge</InvButton>
        </DialogActions>
      </Dialog>

      <ConfirmDialog open={Boolean(dialog)} title={dialog?.title} description={dialog?.desc}
        destructive={dialog?.destructive} requireReason={dialog?.requireReason}
        reasonValue={reason} onReasonChange={setReason} loading={actioning}
        onConfirm={executeAction} onCancel={() => { setDialog(null); setReason(''); }} />
    </Box>
  );
}
